// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';

// Custom packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { Attachment, Recipient } from 'src/app/shared/models/communication/communication.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from 'src/app/shared/services/config.service';

@Component({
  selector: 'app-view-communication-detail-dialog',
  templateUrl: './view-communication-detail-dialog.component.html',
  styleUrls: ['./view-communication-detail-dialog.component.scss']
})
export class ViewCommunicationDetailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('common.cta.send'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-paper-plane',
      inline: true,
    },
  };
  // END - Add communication form

  dialogIsLoading = true;
  recipients: Recipient[] = [];
  recipientsCc: Recipient[] = [];
  recipientsBcc: Recipient[] = [];
  attachments: Attachment[] = [];

  emailVersion2 = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ViewCommunicationDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,

    private cookieService: CookieService,
    private translateService: TranslateService,
    private authService: AuthService,
  ) { }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));

    // Get current lang
    this.lang = this.cookieService.get('lang') || this.configService.defaultLang;

    // Init recipients (if present)
    if (this.data.recipients) {
      this.recipients = this.data.recipients;
    }
    if (this.data.recipientsCc) {
      this.recipientsCc = this.data.recipientsCc;
    }
    if (this.data.recipientsBcc) {
      this.recipientsBcc = this.data.recipientsBcc;
    }

    // Version 2
    if (this.data.emailV2) {
      this.emailVersion2 = true;
      this.initFormV2();
    } else {
      // Get form initial values and init the form
      this.getDataAndInitForm();
    }

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Preload data and then init form
   *
   * @since 1.0.0
   */
  getDataAndInitForm(): void {
    // Init recipients with first recipient and init form
    if (this.data && this.data.recipients) {
      this.recipients = this.data.recipients.map((obj: any) => obj._id);
      this.recipientsCc = this.data.recipientsCc.map((obj: any) => obj._id);
      this.recipientsBcc = this.data.recipientsBcc.map((obj: any) => obj._id);
    }
    if (
      this.data && 
      this.data.createdBy &&
      this.data.createdBy.firstName &&
      this.data.createdBy.lastName
    ) {
      this.data.createdBy = `${this.data.createdBy.firstName} ${this.data.createdBy.lastName}`;
    }

    this.initForm();
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(availableData?: any): void {
    // Set model
    this.model = this.data;
    this.model.body = this.model.body.replace(/<br>/gi, "\n").replace(/<br \/>/gi, "\n").replace(/<br\/>/gi, "\n");
    let listOfLinks = "";
    if (this.model.list) {
      listOfLinks = this.model.list.replace(/<br>/gi, '');
    //   const list = this.model.list.replace("<ul>", "\n\n").replace(/<br>/gi, "\n\n").replace(/(<([^>]+)>)/gi, "");
    //   this.model.body = `${this.model.body}${list}`;
    }
      // Set recipients amount and create
    const index = this.recipients.length - 1;

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'createdBy',
            templateOptions: {
              label: this.translateService.instant('communications.fields.createdBy'),
              required: false,
              minLength: 1,
              maxLength: 500,
              disabled: true,
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'sender',
            templateOptions: {
              label: this.translateService.instant('communications.fields.sender'),
              placeholder: this.configService.ownerEmailNoReply,
              required: false,
              minLength: 1,
              maxLength: 100,
              disabled: true,
              value: this.configService.ownerEmailNoReply,
            },
          },
          {
            className: 'col-12 mb-0 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipients',
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipients'),
              required: true,
              bindLabel: 'email',
              multiple: true,
              readonly: true,
            },
          },
          {
            className: 'col-12 mb-0 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipientsCc',
            hideExpression: (model): boolean => !model.recipientsCc || model.recipientsCc.length === 0,
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipientsCc'),
              required: true,
              bindLabel: 'email',
              multiple: true,
              readonly: true,
            },
          },
          {
            className: 'col-12 autocomplete-multi-email',
            type: 'autocomplete',
            key: 'recipientsBcc',
            hideExpression: (model): boolean => !model.recipientsBcc || model.recipientsBcc.length === 0,
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipientsBcc'),
              required: true,
              bindLabel: 'email',
              multiple: true,
              readonly: true,
            },
          },
          {
            className: 'col-12 pt-4',
            type: 'input',
            key: 'subject',
            templateOptions: {
              label: this.translateService.instant('communications.fields.subject'),
              placeholder: this.translateService.instant('communications.fields.subject'),
              required: false,
              minLength: 1,
              maxLength: 100,
              readonly: true,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'body',
            templateOptions: {
              label: this.translateService.instant('communications.fields.body'),
              placeholder: this.translateService.instant('communications.fields.body'),
              required: true,
              maxLength: 30000,
              rows: 6,
              readonly: true,
            },
          },
          {
            className: "col-12 boxForm",
            template:
              `<div>
              ${listOfLinks}
              </div><br />`,
          },
          {
            className: "col-12",
            type: "attachments",
            key: "attachments",
            hideExpression: (model): boolean => !model.attachments || model.attachments.length === 0,
            templateOptions: {
              label: this.translateService.instant('communications.fields.attachments'),
              required: false,
              bindLabel: "originalName",
              multiple: true,
              readonly: true,
            }
          },
        ],
      },
    ];
  }

  /**
   * Init form version 2
   *
   * @since 1.0.0
   */
  initFormV2(): void {
    if (
      this.data.element && 
      this.data.element.createdBy &&
      this.data.element.createdBy.firstName &&
      this.data.element.createdBy.lastName
    ) {
      this.data.element.createdBy = `${this.data.element.createdBy.firstName} ${this.data.element.createdBy.lastName}`;
    }
    // Set model
    this.model = this.data.element;
    this.model.body = this.model.content
      .replace(/\\n/gi, "\n")
      .replace(/<br>/gi, "\n")
      .replace(/<br \/>/gi, "\n")
      .replace(/<br\/>/gi, "\n")
      .replace(/&#x2F;/g, "/");

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'createdBy',
            templateOptions: {
              label: this.translateService.instant('communications.fields.createdBy'),
              required: false,
              minLength: 1,
              maxLength: 500,
              disabled: true,
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'sender',
            templateOptions: {
              label: this.translateService.instant('communications.fields.sender'),
              placeholder: this.configService.ownerEmailNoReply,
              required: false,
              minLength: 1,
              maxLength: 100,
              disabled: true,
              value: this.configService.ownerEmailNoReply,
            },
          },
          {
            className: 'col-12 mb-0 autocomplete-multi-email',
            type: 'input',
            key: 'emailTo',
            templateOptions: {
              customLabel: this.translateService.instant('communications.fields.recipients'),
              required: true,
              readonly: true,
            },
          },
          {
            className: 'col-12 pt-4',
            type: 'input',
            key: 'subject',
            templateOptions: {
              label: this.translateService.instant('communications.fields.subject'),
              placeholder: this.translateService.instant('communications.fields.subject'),
              required: false,
              minLength: 1,
              maxLength: 100,
              readonly: true,
            },
          },
          {
            className: 'col-12',
            type: 'textarea',
            key: 'body',
            templateOptions: {
              label: this.translateService.instant('communications.fields.body'),
              placeholder: this.translateService.instant('communications.fields.body'),
              required: true,
              maxLength: 30000,
              rows: 6,
              readonly: true,
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   */
  onClose(data?: any): void {
    this.dialogRef.close();
  }
}
